<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem'

const { t } = useL10n()
const user = useUserStore()

const menu = ref()
const hasImageError = ref(false)

const toggle = (event: Event) => menu.value?.toggle(event)

const contactName = computed(() => {
  const contact = user.contact
  const person = `${contact?.vorname || ''} ${contact?.name || ''}`.trim()
  return person || contact?.firma || ''
})

const initials = computed(() => {
  const contact = user.contact
  const letters = `${contact?.vorname?.[0] || ''}${contact?.name?.[0] || ''}`
  return (letters || contact?.firma?.[0] || '?').toUpperCase()
})

const showImage = computed(() => !hasImageError.value && !!user.contact?.profile_picture_url)

const menuItems = computed<MenuItem[]>(() => [
  {
    label: t('global.userMenu.logout'),
    icon: 'pi pi-sign-out',
    command: () => user.contactLogout()
  }
])
</script>

<template>
  <div class="user-menu">
    <button
      type="button"
      class="user-menu-button"
      :aria-label="t('global.userMenu.open')"
      @click="toggle"
    >
      <span v-if="contactName" class="user-greeting">
        <small>{{ t('layout.welcome') }},</small>
        <span class="user-name">{{ contactName }}</span>
      </span>
      <span class="user-avatar">
        <img
          v-if="showImage"
          :src="user.contact.profile_picture_url"
          :alt="contactName"
          @error="hasImageError = true"
        />
        <span v-else>{{ initials }}</span>
      </span>
      <i class="pi pi-angle-down user-caret" aria-hidden="true"></i>
    </button>
    <Menu ref="menu" :model="menuItems" :popup="true" />
  </div>
</template>

<style scoped lang="scss">
.user-menu {
  min-width: 0;
}

.user-menu-button {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  max-width: 100%;
  padding: 0.25rem 0.4rem 0.25rem 0.6rem;
  border: none;
  background: transparent;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--portal-bg);
  }

  &:focus-visible {
    outline: 2px solid var(--portal-primary);
    outline-offset: 2px;
  }
}

.user-greeting {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
  min-width: 0;

  small {
    color: var(--portal-muted);
    font-size: 0.72rem;
  }
}

// Company contacts carry long names ("… Entfeuchtung und Sanierungs GmbH"),
// which would otherwise push the avatar out of the header.
.user-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--portal-text);
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--portal-primary-soft);
  color: var(--portal-primary-strong);
  font-size: 0.8rem;
  font-weight: 700;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.user-caret {
  color: var(--portal-muted);
  font-size: 0.8rem;
}

@media (max-width: 640px) {
  .user-greeting {
    display: none;
  }
}
</style>
